import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import { workspacesApi } from "../../actions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import { ConfirmDialog, EmptyState, Hint, ListSkeleton, Notice, Refusal, Surface, dialogPaper, focusRing, quietDanger, rowSx } from "./WorkspaceUi";
import { formatDate } from "./workspaceFormat";

const count = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString("en-GB") : "0");

// Copying may be refused (an insecure origin, an old browser): the key stays
// on screen either way, so it can still be selected by hand.
const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    return false;
  }
};

function KeyRow({ apiKey, limits, isStaff, mayChange, onRevoke, first }) {
  const revoked = apiKey.status === "revoked";
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ sm: "center" }}
      sx={{ ...rowSx(first), opacity: revoked ? 0.6 : 1 }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          bgcolor: revoked ? brand.shell : notice.good.bg,
          color: revoked ? brand.muted : notice.good.fg,
          flexShrink: 0,
        }}
      >
        <KeyRoundedIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 650, letterSpacing: "-0.01em", overflowWrap: "anywhere" }}>{apiKey.name}</Typography>
          <Chip
            size="small"
            variant="outlined"
            label={revoked ? "Revoked" : "Active"}
            sx={{
              height: 22,
              fontSize: "0.74rem",
              fontWeight: 600,
              color: revoked ? brand.muted : notice.good.fg,
              bgcolor: revoked ? brand.shell : notice.good.bg,
              borderColor: revoked ? brand.line : notice.good.line,
            }}
          />
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: brand.muted, bgcolor: brand.shell, border: `1px solid ${brand.line}`, borderRadius: "6px", px: 0.6, overflowWrap: "anywhere" }}
          >
            bfb_live_{apiKey.prefix}_…
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: brand.muted, mt: 0.35 }}>
          {count(apiKey.requests?.count)} of {count(limits?.requestsPerMonth)} this month
          {apiKey.lastUsedAt ? ` · last used ${formatDate(apiKey.lastUsedAt)}` : " · never used"}
          {revoked ? ` · revoked ${formatDate(apiKey.revokedAt)}` : ""}
          {isStaff && apiKey.createdBy ? ` · created by ${apiKey.createdBy}` : ""}
        </Typography>
      </Box>
      {mayChange && !revoked && (
        <Button
          variant="outlined"
          size="small"
          aria-label={`Revoke ${apiKey.name}`}
          onClick={() => onRevoke(apiKey)}
          sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" }, ...quietDanger }}
        >
          Revoke
        </Button>
      )}
    </Stack>
  );
}

// A workspace's API keys: what each one is, how much it has used this month,
// and revoking one. The key itself exists on screen only once, right after it
// is created.
export default function ApiKeysTab({ workspace, mayChange = true, isStaff = false, notify }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    const res = await workspacesApi.apiKeys(workspace.id);
    if (res.success) {
      setState(res.content);
      setError("");
    } else setError(res.msg || "Couldn't load the API keys.");
  }, [workspace.id]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (event) => {
    if (event) event.preventDefault();
    setBusy(true);
    const res = await workspacesApi.createApiKey(workspace.id, name.trim());
    setBusy(false);
    if (!res.success) {
      setError(res.msg || "Couldn't create the key.");
      return;
    }
    setCreating(false);
    setName("");
    setCopied(false);
    setCreated(res.content);
    load();
  };

  const canCreate = mayChange && state?.access;

  return (
    <Box sx={focusRing}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: brand.prose, lineHeight: 1.65, maxWidth: 580 }}>
            A key reads this workspace's databases through the API, and nothing else. It is read-only, shown once, and can be revoked at any time.
          </Typography>
          {state?.access && (
            <Typography variant="body2" sx={{ color: brand.muted, fontSize: "0.85rem", mt: 0.5, maxWidth: 580 }}>
              This plan allows {count(state.limits?.requestsPerMonth)} requests a month and {count(state.limits?.rateLimitPerMinute)} a minute, per
              key.
            </Typography>
          )}
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreating(true)}>
            New key
          </Button>
        )}
      </Stack>

      {error && (
        <Refusal onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Refusal>
      )}

      {state && !state.access && (
        <Hint
          title={`The ${state.planName || state.plan} plan doesn't include the API`}
          next="Ask bpExperts about a plan that does, and keys can be created here."
          sx={{ mb: 2 }}
        >
          Nothing is missing from this workspace - the API is simply not part of the plan it is on.
        </Hint>
      )}

      {!state ? (
        <ListSkeleton rows={2} />
      ) : (
        <Surface>
          {state.keys.length === 0 ? (
            <EmptyState icon={<KeyRoundedIcon />} title="No keys yet">
              {canCreate
                ? "An API key lets one of your own systems read this workspace's process library - a reporting tool, an intranet page, a script. Create one for each system, so a single one can be revoked."
                : "An API key lets one of your own systems read this workspace's process library. When an admin creates one, it shows here."}
            </EmptyState>
          ) : (
            state.keys.map((apiKey, index) => (
              <KeyRow
                key={apiKey.id}
                apiKey={apiKey}
                limits={state.limits}
                isStaff={isStaff}
                mayChange={mayChange}
                onRevoke={setRevoking}
                first={index === 0}
              />
            ))
          )}
        </Surface>
      )}

      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="xs" PaperProps={dialogPaper}>
        <form onSubmit={create}>
          <DialogTitle sx={{ fontWeight: 700 }}>New API key</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Name"
              helperText="What will use it - for example Reporting, or the name of the system."
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <LoadingButton type="submit" variant="contained" loading={busy} disabled={!name.trim()}>
              Create key
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={Boolean(created)} onClose={() => setCreated(null)} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
        <DialogTitle sx={{ fontWeight: 700 }}>{created?.apiKey?.name} is ready</DialogTitle>
        <DialogContent>
          <Notice
            tone={notice.wait}
            icon={<LockClockRoundedIcon fontSize="small" />}
            title="Copy it now - it won't be shown again"
            sx={{ mb: 2 }}
          >
            Keep it somewhere safe, like the password manager your team uses. If you lose it, revoke it and create another.
          </Notice>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: brand.shell,
                border: `1px solid ${brand.line}`,
                fontFamily: "monospace",
                fontSize: "0.85rem",
                color: brand.ink,
                overflowWrap: "anywhere",
              }}
            >
              {created?.key}
            </Box>
            <Tooltip title={copied ? "Copied" : "Copy"}>
              <IconButton
                aria-label="Copy key"
                onClick={async () => {
                  const done = await copy(created.key);
                  setCopied(done);
                  if (done && notify) notify("Key copied.");
                }}
              >
                <ContentCopyRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" sx={{ color: brand.muted, mt: 2, lineHeight: 1.65 }}>
            Send it as{" "}
            <Box
              component="code"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.85em",
                color: brand.inkSoft,
                bgcolor: brand.shell,
                border: `1px solid ${brand.line}`,
                borderRadius: "6px",
                px: 0.55,
                py: 0.15,
              }}
            >
              Authorization: Bearer {"<key>"}
            </Box>
            . The Developers page shows every endpoint and lets you try one.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setCreated(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(revoking)}
        title={`Revoke ${revoking?.name}?`}
        body="Anything using this key stops working at once, and this can't be undone. The key's record stays, so you can see it was revoked and by whom."
        confirmLabel="Revoke"
        onConfirm={async () => {
          const res = await workspacesApi.revokeApiKey(workspace.id, revoking.id);
          if (res.success) {
            if (notify) notify(res.msg);
            load();
          }
          return res;
        }}
        onClose={() => setRevoking(null)}
      />
    </Box>
  );
}
